// @dynamic-i18n-keys
// Dynamic t() at lines 62–68, 70–71, 158–161, 164, 167 (TRANSLATION_KEYS[gallectionType]; groups.submit at 112–116):
//   pages.gallections.gallery.emptyState.title.owner
//   pages.gallections.gallery.emptyState.title.visitor
//   pages.gallections.gallery.emptyState.subtitle.owner
//   pages.gallections.gallery.emptyState.subtitle.visitor
//   pages.gallections.gallery.emptyState.buttonCTA.owner
//   pages.gallections.collection.emptyState.title.owner
//   pages.gallections.collection.emptyState.title.visitor
//   pages.gallections.collection.emptyState.subtitle.owner
//   pages.gallections.collection.emptyState.subtitle.visitor
//   pages.gallections.collection.emptyState.buttonCTA.owner
//   pages.gallections.privateCollection.emptyState.title.owner
//   pages.gallections.privateCollection.emptyState.subtitle.owner
//   pages.gallections.privateCollection.emptyState.buttonCTA.owner
//   groups.submit.contributeExistingDeviation
//   groups.submit.recommendExistingDeviation
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { URLS, Url, replaceWithSanitizedUrl } from '@wix/da-url';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import FavouriteOutfilled from '@wix/da-ds/pkg/Icons/FavouriteOutfilled/FavouriteOutfilled';
import DreamUpStars from '@wix/da-ds/pkg/Icons/16x16/DreamUpStars';
import { BiData, SubmitButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton/GhostButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import GhostButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/GhostButtonWithContextMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import MediaSelectorWithGroupGallection from '@wix/da-shared-react/pkg/MediaPopup/Groups/MediaSelectorWithGroupGallection';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { GroupGallectionType } from '@wix/da-shared-react/pkg/GroupGallectionSelector/redux/types';

import EmptyState from '../../../EmptyState';
import { GallectionType } from '../../../../../types/gallection';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { getSubmitUrlForProfileOwner } from '../../../../utils/urls';
import s from './GallectionsEmptyState.scss';

const GALLERY_EMPTY_ILLUSTRATION_URL =
  'https://st.deviantart.net/eclipse/illustrations/gallery.png';
const FAVOURITES_EMPTY_ILLUSTRATION_URL =
  'https://st.deviantart.net/eclipse/illustrations/favourites.png';
const PRIVATE_COLLECTION_EMPTY_ILLUSTRATION_URL =
  'https://st.deviantart.net/eclipse/illustrations/private.png';

const TRANSLATION_KEYS = {
  [GallectionType.GALLERY]: {
    titleOwner: 'pages.gallections.gallery.emptyState.title.owner',
    titleOwnerV2: 'pages.gallections.gallery.emptyState.title.owner.v2',
    titleVisitor: 'pages.gallections.gallery.emptyState.title.visitor',
    titleVisitorV2: 'pages.gallections.gallery.emptyState.title.visitor.v2',
    subtitleOwner: 'pages.gallections.gallery.emptyState.subtitle.owner',
    subtitleOwnerV2: 'pages.gallections.gallery.emptyState.subtitle.owner.v2',
    subtitleVisitor: 'pages.gallections.gallery.emptyState.subtitle.visitor',
    subtitleVisitorV2: 'pages.gallections.gallery.emptyState.subtitle.visitor.v2',
    buttonCTA: 'pages.gallections.gallery.emptyState.buttonCTA.owner',
    createArtButton: 'pages.gallections.gallery.emptyState.createArt',
    submitDeviationButton: 'pages.gallections.gallery.emptyState.submitDeviation',
    createArtDropdown: {
      dreamUp: 'pages.gallections.gallery.emptyState.createArt.dreamUp',
      draw: 'pages.gallections.gallery.emptyState.createArt.draw',
    },
  },
  [GallectionType.COLLECTION]: {
    titleOwner: 'pages.gallections.collection.emptyState.title.owner',
    titleOwnerV2: 'pages.gallections.collection.emptyState.title.owner.v2',
    titleVisitor: 'pages.gallections.collection.emptyState.title.visitor',
    titleVisitorV2: 'pages.gallections.collection.emptyState.title.visitor.v2',
    subtitleOwner: 'pages.gallections.collection.emptyState.subtitle.owner',
    subtitleOwnerV2: 'pages.gallections.collection.emptyState.subtitle.owner.v2',
    subtitleVisitor: 'pages.gallections.collection.emptyState.subtitle.visitor',
    subtitleVisitorV2: 'pages.gallections.collection.emptyState.subtitle.visitor.v2',
    buttonCTA: 'pages.gallections.collection.emptyState.buttonCTA.owner',
    exploreArtButton: 'pages.gallections.collection.emptyState.exploreArt',
  },
  [GallectionType.PRIVATE_COLLECTION]: {
    titleOwner: 'pages.gallections.privateCollection.emptyState.title.owner',
    titleOwnerV2: 'pages.gallections.privateCollection.emptyState.title.owner.v2',
    subtitleOwner: 'pages.gallections.privateCollection.emptyState.subtitle.owner',
    subtitleOwnerV2: 'pages.gallections.privateCollection.emptyState.subtitle.owner.v2',
    buttonCTA: 'pages.gallections.privateCollection.emptyState.buttonCTA.owner',
    exploreArtButton: 'pages.gallections.privateCollection.emptyState.exploreArt',
  },
};

export interface Props {
  gallectionType: GallectionType;
  owner: PapiUser;
  isOwner: boolean;
  canSubmitDeviations: boolean;
  contributeDeviations: (
    deviations: PapiDeviation[],
    group: PapiUser,
    type: GroupGallectionType,
    folderId?: number
  ) => void;
}

function GallectionsEmptyState(props: Props) {
  const { t } = useTranslation();
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const { isOwner, canSubmitDeviations, gallectionType, owner, contributeDeviations } = props;

  const onButtonClick = useCallback(() => {
    if (gallectionType === GallectionType.GALLERY) {
      replaceWithSanitizedUrl(getSubmitUrlForProfileOwner(owner));
    } else {
      window.location.replace(URLS.ROOT);
    }
  }, [gallectionType, owner]);

  const openMediaSelector = useCallback(() => {
    setIsMediaSelectorOpen(true);
  }, []);

  const closeMediaSelector = useCallback(() => {
    setIsMediaSelectorOpen(false);
  }, []);

  const getSecondaryButton = () => {
    const isGroupGallery = gallectionType === GallectionType.GALLERY && isGroup(owner);

    if (!canSubmitDeviations || !isGroupGallery) {
      return null;
    }

    return (
      <>
        <GhostButton variant="main" size="medium" onClick={openMediaSelector}>
          {t(
            canSubmitDeviations
              ? 'groups.submit.contributeExistingDeviation'
              : 'groups.submit.recommendExistingDeviation'
          )}
        </GhostButton>
        {isMediaSelectorOpen && (
          <MediaSelectorWithGroupGallection
            group={owner}
            allowUploads={false}
            mediaSourceTypes={[
              MediaSourceType.GALLERY,
              MediaSourceType.COLLECTION,
              MediaSourceType.JOURNAL,
              MediaSourceType.STATUS,
              MediaSourceType.BROWSE,
            ]}
            onClose={closeMediaSelector}
            onSelect={(...args: any[]) => {
              contributeDeviations(
                ...(args as [PapiDeviation[], PapiUser, GroupGallectionType, number?])
              );
              closeMediaSelector();
              window.location.reload();
            }}
          />
        )}
      </>
    );
  };

  if (gallectionType === GallectionType.PRIVATE_COLLECTION && !isOwner) {
    return null;
  }

  // Feature flag controlled empty states
  const isGalleryOrCollection =
    gallectionType === GallectionType.GALLERY || gallectionType === GallectionType.COLLECTION;

  // Owner viewing empty gallery with new empty state flag
  const showNewOwnerGalleryEmptyState = isOwner && gallectionType === GallectionType.GALLERY;

  // Owner viewing empty collection (Favourites/Exclusives) with new empty state flag
  const showNewOwnerCollectionEmptyState = isOwner && gallectionType === GallectionType.COLLECTION;

  // Owner viewing empty private collection with new empty state flag
  const showNewOwnerPrivateCollectionEmptyState =
    isOwner && gallectionType === GallectionType.PRIVATE_COLLECTION;

  // Visitor viewing empty gallery/collection with new empty state flag
  const showNewVisitorEmptyState = !isOwner && isGalleryOrCollection;

  const showNewOwnerEmptyState =
    showNewOwnerGalleryEmptyState ||
    showNewOwnerCollectionEmptyState ||
    showNewOwnerPrivateCollectionEmptyState;

  const showNewEmptyState = showNewOwnerEmptyState || showNewVisitorEmptyState;

  const getIllustrationUrl = () => {
    if (gallectionType === GallectionType.GALLERY) {
      return GALLERY_EMPTY_ILLUSTRATION_URL;
    }
    if (gallectionType === GallectionType.COLLECTION) {
      // Both owner and visitor see favourites illustration
      return FAVOURITES_EMPTY_ILLUSTRATION_URL;
    }
    if (gallectionType === GallectionType.PRIVATE_COLLECTION) {
      return PRIVATE_COLLECTION_EMPTY_ILLUSTRATION_URL;
    }
    return '';
  };

  const getTitle = () => {
    if (showNewOwnerGalleryEmptyState) {
      return t(TRANSLATION_KEYS[GallectionType.GALLERY].titleOwnerV2);
    }
    if (showNewOwnerCollectionEmptyState) {
      return t(TRANSLATION_KEYS[GallectionType.COLLECTION].titleOwnerV2);
    }
    if (showNewOwnerPrivateCollectionEmptyState) {
      return t(TRANSLATION_KEYS[GallectionType.PRIVATE_COLLECTION].titleOwnerV2);
    }
    if (isOwner) {
      return t(TRANSLATION_KEYS[gallectionType].titleOwner);
    }
    if (showNewVisitorEmptyState) {
      return t(TRANSLATION_KEYS[gallectionType].titleVisitorV2);
    }
    return t(TRANSLATION_KEYS[gallectionType].titleVisitor, {
      username: owner.username,
    });
  };

  const getSubtitleContent = () => {
    if (showNewOwnerGalleryEmptyState) {
      return t(TRANSLATION_KEYS[GallectionType.GALLERY].subtitleOwnerV2);
    }
    if (showNewOwnerCollectionEmptyState) {
      return t(TRANSLATION_KEYS[GallectionType.COLLECTION].subtitleOwnerV2);
    }
    if (showNewOwnerPrivateCollectionEmptyState) {
      return t(TRANSLATION_KEYS[GallectionType.PRIVATE_COLLECTION].subtitleOwnerV2);
    }
    if (isOwner) {
      return gallectionType === GallectionType.COLLECTION
        ? t(TRANSLATION_KEYS[gallectionType].subtitleOwner, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            starIcon: <FavouriteOutfilled size={IconSize.SMALL} className={s['star']} />,
          })
        : t(TRANSLATION_KEYS[gallectionType].subtitleOwner);
    }
    if (showNewVisitorEmptyState) {
      return t(TRANSLATION_KEYS[gallectionType].subtitleVisitorV2, {
        username: owner.username,
      });
    }
    return t(TRANSLATION_KEYS[gallectionType].subtitleVisitor, {
      username: owner.username,
    });
  };

  // New owner empty state buttons with dropdown
  const getOwnerButtons = () => {
    // Gallery owner - Create Art dropdown + Submit Deviation button
    if (showNewOwnerGalleryEmptyState) {
      const dropdownItems: NullableDropdownItem[] = [
        {
          label: t(TRANSLATION_KEYS[GallectionType.GALLERY].createArtDropdown.dreamUp),
          link: Url.dreamupLink(),
        },
        {
          label: t(TRANSLATION_KEYS[GallectionType.GALLERY].createArtDropdown.draw),
          link: Url.muroLink(),
        },
      ];

      return (
        <div className={s['owner-buttons']}>
          <GhostButtonWithContextMenu
            items={dropdownItems}
            size="medium"
            variant="main"
            icon={DreamUpStars}
          >
            {t(TRANSLATION_KEYS[GallectionType.GALLERY].createArtButton)}
          </GhostButtonWithContextMenu>
          <BrandedButton
            variant="brand-green"
            size="medium"
            onClick={onButtonClick}
            biData={BiData<SubmitButtonClickBiEvent>({
              evid: 704,
              sectionname: gallectionType,
            })}
          >
            {t(TRANSLATION_KEYS[GallectionType.GALLERY].submitDeviationButton)}
          </BrandedButton>
        </div>
      );
    }

    // Collection (Favourites/Exclusives) owner - Explore Art button
    if (showNewOwnerCollectionEmptyState) {
      return (
        <BrandedButton
          variant="brand-green"
          size="medium"
          onClick={onButtonClick}
          biData={BiData<SubmitButtonClickBiEvent>({
            evid: 704,
            sectionname: gallectionType,
          })}
        >
          {t(TRANSLATION_KEYS[GallectionType.COLLECTION].exploreArtButton)}
        </BrandedButton>
      );
    }

    // Private Collection owner - Explore Art button
    if (showNewOwnerPrivateCollectionEmptyState) {
      return (
        <BrandedButton
          variant="brand-green"
          size="medium"
          onClick={onButtonClick}
          biData={BiData<SubmitButtonClickBiEvent>({
            evid: 704,
            sectionname: gallectionType,
          })}
        >
          {t(TRANSLATION_KEYS[GallectionType.PRIVATE_COLLECTION].exploreArtButton)}
        </BrandedButton>
      );
    }

    return null;
  };

  return (
    <EmptyState
      view="large"
      className={classnames(s['root'], showNewEmptyState && s['visitor-empty-state'])}
      backgroundType={showNewEmptyState ? 'none' : 'solid'}
      illustration={
        showNewEmptyState ? (
          <img src={getIllustrationUrl()} alt="" className={s['gallery-illustration']} />
        ) : undefined
      }
      title={getTitle()}
      titleClassName={showNewEmptyState ? s['visitor-title'] : undefined}
      subtitle={getSubtitleContent()}
      subtitleClassName={showNewEmptyState ? s['visitor-subtitle'] : undefined}
      buttonVariant="approval-green"
      buttonLabel={t(TRANSLATION_KEYS[gallectionType].buttonCTA)}
      biData={BiData<SubmitButtonClickBiEvent>({
        evid: 704,
        sectionname: gallectionType,
      })}
      onButtonClick={onButtonClick}
      withButton={isOwner && !showNewOwnerEmptyState}
      customButton={getOwnerButtons()}
      customSecondaryButton={showNewOwnerEmptyState ? null : getSecondaryButton()}
      buttonAlignment="vertical"
    />
  );
}

export default GallectionsEmptyState;
